import { useState, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FaTimes, FaSave, FaShoppingCart, FaPlus, FaTrash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import './CompraFormModal.css';
import { CREAR_COMPRA, GET_COMPRAS } from '../../graphql/compras';
import { GET_PROVEEDORES } from '../../graphql/proveedores';
import { GET_ITEMS_INVENTARIO } from '../../graphql/inventario';
import FileUpload from '../shared/FileUpload';

const today = () => new Date().toISOString().slice(0, 10);

const DETALLE_VACIO = {
  item_inventario_id: '',
  nombre_item: '',
  cantidad: '1',
  precio_unitario: '',
  iva_porcentaje: '19',
  descuento: '0'
};

// Usage:
// <CompraFormModal isOpen={open} onClose={() => setOpen(false)} />

const CompraFormModal = ({ isOpen, onClose }) => {
  const [header, setHeader] = useState({
    proveedor_id: '',
    fecha: today(),
    metodo_pago: 'transferencia',
    referencia_pago: '',
    numero_factura_proveedor: '',
    notas: '',
    comprobante_url: null
  });

  const [detalles, setDetalles] = useState([{ ...DETALLE_VACIO }]);

  const { data: dataProveedores } = useQuery(GET_PROVEEDORES, {
    variables: { activo: true },
    skip: !isOpen
  });

  const { data: dataItems } = useQuery(GET_ITEMS_INVENTARIO, {
    variables: { tipo: 'producto', activo: true },
    skip: !isOpen
  });

  const [crearCompra, { loading }] = useMutation(CREAR_COMPRA, {
    refetchQueries: [{ query: GET_COMPRAS }]
  });

  const proveedores = dataProveedores?.proveedores || [];
  const items = dataItems?.itemsInventario || [];

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetalleChange = (index, field, value) => {
    setDetalles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-fill nombre from inventory item
      if (field === 'item_inventario_id') {
        const item = items.find((i) => i.id.toString() === value);
        if (item) {
          updated[index].nombre_item = item.nombre;
          updated[index].precio_unitario = item.precio_compra?.toString() || '';
          updated[index].iva_porcentaje = item.iva_porcentaje?.toString() || '19';
        }
      }

      return updated;
    });
  };

  const addDetalle = () => {
    setDetalles((prev) => [...prev, { ...DETALLE_VACIO }]);
  };

  const removeDetalle = (index) => {
    if (detalles.length === 1) {
      toast.error('Debe haber al menos una linea de detalle');
      return;
    }
    setDetalles((prev) => prev.filter((_, i) => i !== index));
  };

  const calcLineTotals = (d) => {
    const qty = parseFloat(d.cantidad) || 0;
    const price = parseFloat(d.precio_unitario) || 0;
    const ivaPct = parseFloat(d.iva_porcentaje) || 0;
    const discPct = parseFloat(d.descuento) || 0;
    const subtotalBruto = qty * price;
    const descuentoMonto = subtotalBruto * (discPct / 100);
    const subtotal = subtotalBruto - descuentoMonto;
    const iva = subtotal * (ivaPct / 100);
    return { subtotal, iva, total: subtotal + iva };
  };

  const { grandSubtotal, grandIva, grandTotal } = useMemo(() => {
    return detalles.reduce(
      (acc, d) => {
        const { subtotal, iva, total } = calcLineTotals(d);
        return {
          grandSubtotal: acc.grandSubtotal + subtotal,
          grandIva: acc.grandIva + iva,
          grandTotal: acc.grandTotal + total
        };
      },
      { grandSubtotal: 0, grandIva: 0, grandTotal: 0 }
    );
  }, [detalles]);

  const fmt = (n) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(n);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!header.proveedor_id) {
      toast.error('Debe seleccionar un proveedor');
      return;
    }

    const lineasValidas = detalles.filter(
      (d) => d.nombre_item.trim() && parseFloat(d.cantidad) > 0 && parseFloat(d.precio_unitario) >= 0
    );

    if (lineasValidas.length === 0) {
      toast.error('Debe agregar al menos una linea de detalle valida');
      return;
    }

    try {
      const input = {
        proveedor_id: parseInt(header.proveedor_id),
        fecha: header.fecha,
        metodo_pago: header.metodo_pago || null,
        referencia_pago: header.referencia_pago.trim() || null,
        numero_factura_proveedor: header.numero_factura_proveedor.trim() || null,
        notas: header.notas.trim() || null,
        comprobante_url: header.comprobante_url || null,
        detalles: lineasValidas.map((d) => ({
          item_inventario_id: d.item_inventario_id ? parseInt(d.item_inventario_id) : null,
          nombre_item: d.nombre_item.trim(),
          cantidad: parseFloat(d.cantidad),
          precio_unitario: parseFloat(d.precio_unitario),
          iva_porcentaje: parseFloat(d.iva_porcentaje) || 0,
          descuento: parseFloat(d.descuento) || 0
        }))
      };

      await crearCompra({ variables: { input } });
      toast.success('Orden de compra creada correctamente');
      onClose();
    } catch (error) {
      console.error('Error al crear compra:', error);
      toast.error(error.message || 'Error al crear la orden de compra');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="compra-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="compra-form-header">
          <h2>
            <FaShoppingCart />
            Nueva Orden de Compra
          </h2>
          <button className="close-button" onClick={onClose} type="button" aria-label="Cerrar">
            <FaTimes />
          </button>
        </div>

        <form className="compra-form" onSubmit={handleSubmit} noValidate>
          {/* Encabezado */}
          <div className="form-section">
            <h3>Datos de la Orden</h3>

            <div className="form-row">
              <div className="form-group form-group--wide">
                <label htmlFor="cf-proveedor">Proveedor *</label>
                <select
                  id="cf-proveedor"
                  value={header.proveedor_id}
                  onChange={(e) => handleHeaderChange('proveedor_id', e.target.value)}
                  required
                >
                  <option value="">Seleccione un proveedor</option>
                  {proveedores.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre_comercial || p.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="cf-fecha">Fecha *</label>
                <input
                  id="cf-fecha"
                  type="date"
                  value={header.fecha}
                  onChange={(e) => handleHeaderChange('fecha', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cf-metodo-pago">Metodo de Pago</label>
                <select
                  id="cf-metodo-pago"
                  value={header.metodo_pago}
                  onChange={(e) => handleHeaderChange('metodo_pago', e.target.value)}
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="cheque">Cheque</option>
                  <option value="credito">Credito</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="cf-referencia">Referencia de Pago</label>
                <input
                  id="cf-referencia"
                  type="text"
                  value={header.referencia_pago}
                  onChange={(e) => handleHeaderChange('referencia_pago', e.target.value)}
                  placeholder="No. comprobante, transferencia..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="cf-factura-prov">No. Factura Proveedor</label>
                <input
                  id="cf-factura-prov"
                  type="text"
                  value={header.numero_factura_proveedor}
                  onChange={(e) => handleHeaderChange('numero_factura_proveedor', e.target.value)}
                  placeholder="Ej: FAC-2024-001"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="cf-notas">Notas</label>
              <textarea
                id="cf-notas"
                value={header.notas}
                onChange={(e) => handleHeaderChange('notas', e.target.value)}
                placeholder="Instrucciones de entrega, condiciones especiales..."
                rows="2"
              />
            </div>

            <FileUpload
              value={header.comprobante_url}
              onChange={(url) => handleHeaderChange('comprobante_url', url)}
              label="Factura / Documento del proveedor"
            />
          </div>

          {/* Lineas de detalle */}
          <div className="form-section form-section--last">
            <div className="detalles-header">
              <h3>Articulos</h3>
              <button
                type="button"
                className="btn-add-linea"
                onClick={addDetalle}
              >
                <FaPlus /> Agregar Linea
              </button>
            </div>

            <div className="detalles-table-wrapper">
              <table className="detalles-table">
                <thead>
                  <tr>
                    <th>Item Inventario</th>
                    <th>Nombre / Descripcion</th>
                    <th>Cant.</th>
                    <th>Precio Unit.</th>
                    <th>IVA %</th>
                    <th>Desc. %</th>
                    <th>Subtotal</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {detalles.map((detalle, index) => {
                    const { subtotal, total } = calcLineTotals(detalle);
                    return (
                      <tr key={index}>
                        <td>
                          <select
                            value={detalle.item_inventario_id}
                            onChange={(e) =>
                              handleDetalleChange(index, 'item_inventario_id', e.target.value)
                            }
                          >
                            <option value="">Manual</option>
                            {items.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.codigo ? `[${item.codigo}] ` : ''}{item.nombre}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={detalle.nombre_item}
                            onChange={(e) =>
                              handleDetalleChange(index, 'nombre_item', e.target.value)
                            }
                            placeholder="Nombre del articulo"
                            required
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={detalle.cantidad}
                            onChange={(e) =>
                              handleDetalleChange(index, 'cantidad', e.target.value)
                            }
                            min="0.001"
                            step="0.001"
                            className="input-number"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={detalle.precio_unitario}
                            onChange={(e) =>
                              handleDetalleChange(index, 'precio_unitario', e.target.value)
                            }
                            min="0"
                            step="0.01"
                            placeholder="0"
                            className="input-number"
                          />
                        </td>
                        <td>
                          <select
                            value={detalle.iva_porcentaje}
                            onChange={(e) =>
                              handleDetalleChange(index, 'iva_porcentaje', e.target.value)
                            }
                            className="select-small"
                          >
                            <option value="0">0%</option>
                            <option value="5">5%</option>
                            <option value="19">19%</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={detalle.descuento}
                            onChange={(e) =>
                              handleDetalleChange(index, 'descuento', e.target.value)
                            }
                            min="0"
                            max="100"
                            step="0.01"
                            className="input-number input-number--small"
                          />
                        </td>
                        <td className="text-right monto-subtotal">{fmt(subtotal)}</td>
                        <td className="text-right monto-total">{fmt(total)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-remove-linea"
                            onClick={() => removeDetalle(index)}
                            title="Eliminar linea"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totales */}
            <div className="compra-totales">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>{fmt(grandSubtotal)}</span>
              </div>
              <div className="total-row">
                <span>IVA:</span>
                <span>{fmt(grandIva)}</span>
              </div>
              <div className="total-row total-row--grand">
                <span>TOTAL:</span>
                <span>{fmt(grandTotal)}</span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-guardar" disabled={loading}>
              <FaSave />
              {loading ? 'Creando...' : 'Crear Orden de Compra'}
            </button>
            <button type="button" className="btn-cancelar" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompraFormModal;
